import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SignInButton, SignUpButton, useUser } from '@clerk/clerk-react';
import { setCurrentPage } from '../store/slices/uiSlice';
import { Menu, X, Zap, Bell } from 'lucide-react';

const EnhancedLandingNavigation = () => {
  const dispatch = useDispatch();
  const { user, isSignedIn } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(0);
  
  const { currentPage } = useSelector((state) => state.ui);
  const subscription = useSelector((state) => state.subscription);

  useEffect(() => {
    setCurrentStreak(Math.floor(Math.random() * 50) + 1);
  }, []);

  const isDev = process.env.NODE_ENV !== 'production';

  const navigationItems = [
    { page: 'home', label: 'Home', public: true },
    { page: 'pricing', label: 'Pricing', public: true },
    ...(isDev ? [{ page: 'devpayments', label: 'Dev Payments', public: false }] : []),
  ];

  const handleNavigation = (page) => {
    dispatch(setCurrentPage(page));
    setIsOpen(false);
  };

  const isActive = (page) => {
    if (page === 'home') return currentPage === 'home' || currentPage === '';
    return currentPage === page;
  };

  const planName = subscription?.plan_type === 'yearly' ? 'Yearly Plan' : 'Monthly Plan';

  return (
    <>
      <nav className="bg-white/90 backdrop-blur-md border-b border-gray-200/20 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Enhanced Logo */}
            <button 
              onClick={() => handleNavigation('home')}
              className="flex items-center space-x-3 group"
            >
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-200">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full animate-pulse"></div>
              </div>
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  StreakKeeper
                </span>
                {isSignedIn && currentStreak > 0 && (
                  <div className="text-xs text-gray-500 -mt-1">
                    {currentStreak} day streak 🔥
                  </div>
                )}
              </div>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navigationItems.map((item) => {
                if (!item.public && !isSignedIn) return null;
                
                return (
                  <button
                    key={item.page}
                    onClick={() => handleNavigation(item.page)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive(item.page) 
                        ? 'text-blue-600 bg-blue-50 shadow-sm' 
                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <SignInButton mode="modal">
                  <button className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                    Sign In
                  </button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25">
                    Get Started
                  </button>
                </SignUpButton>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-gray-700 hover:text-blue-600 transition-colors p-2"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="md:hidden border-t border-gray-200/20 bg-white/95 backdrop-blur-sm">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navigationItems.map((item) => {
                  if (!item.public && !isSignedIn) return null;
                  
                  return (
                    <button
                      key={item.page}
                      onClick={() => handleNavigation(item.page)}
                      className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                        isActive(item.page) 
                          ? 'text-blue-600 bg-blue-50' 
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
                
                {/* Mobile Auth Section */}
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                  <SignInButton mode="modal">
                    <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-md font-medium">
                      Sign In
                    </button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg font-medium">
                      Get Started
                    </button>
                  </SignUpButton>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default EnhancedLandingNavigation;